/**
 * AdSense Component
 * 
 * A reusable Google AdSense ad component compatible with Vite PWA React TypeScript apps.
 * 
 * Configuration Required:
 * 1. Update data-ad-client below with your AdSense publisher ID
 *    Format: ca-pub-XXXXXXXXXXXXXXXX
 *    Get it from: https://www.google.com/adsense/ → Account → Account information
 * 
 * 2. Pass the ad slot ID as a prop
 *    Get slot IDs from: https://www.google.com/adsense/ → Ads → By ad unit
 * 
 * Features:
 * - Prevents duplicate ad loading with useRef
 * - Responsive by default
 * - Error handling for missing adsbygoogle
 * - PWA and service worker compatible
 * 
 * Usage:
 * ```tsx
 * <AdSense adSlot="1234567890" adFormat="auto" fullWidthResponsive={true} />
 * ```
 */
import React, { useRef, useLayoutEffect } from 'react';
import './AdSense.css';

interface AdSenseProps {
    adSlot?: string;
    adFormat?: string;
    fullWidthResponsive?: boolean;
    style?: React.CSSProperties;
}
const AdSense: React.FC<AdSenseProps> = ({
    adSlot = '4516574824',
    adFormat = 'auto',
    fullWidthResponsive = true,
    style = {}
}) => {
    const adRef = useRef<HTMLModElement>(null);
    const isAdPushed = useRef(false);

    // useLayoutEffect runs synchronously after DOM mutations but before paint
    // This ensures the DOM is ready and has dimensions
    useLayoutEffect(() => {
        // Only push ads once per component instance
        if (isAdPushed.current) {
            return;
        }

        const pushAd = () => {
            try {
                // Check if adsbygoogle is available
                const adsbygoogle = (window as any).adsbygoogle || [];
                
                // Verify the container exists and is empty
                if (adRef.current && adRef.current.innerHTML === '') {
                    adsbygoogle.push({});
                    isAdPushed.current = true;
                }
            } catch (error) {
                console.error('AdSense error:', error);
            }
        };

        // Push the ad - the DOM is guaranteed to be ready in useLayoutEffect
        pushAd();
    }, []);

    return (
        <div className="adsense-container" style={style}>
            <ins
                ref={adRef}
                className="adsbygoogle"
                style={{ display: 'block' }}
                data-ad-client="ca-pub-4868973749453562"
                data-ad-slot={adSlot}
                data-ad-format={adFormat}
                data-full-width-responsive={fullWidthResponsive.toString()}
            />
        </div>
    );
};

export default AdSense;

